import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from 'react'

type BeforeNavigate = () => boolean | void
type Navigation = {
  register: (guard: BeforeNavigate) => () => void
  mayNavigate: () => boolean
}

const Context = createContext<Navigation | null>(null)

export function CoachNavigation({ children }: { children: ReactNode }) {
  const guard = useRef<BeforeNavigate | null>(null)
  const register = useCallback((next: BeforeNavigate) => {
    guard.current = next
    return () => {
      if (guard.current === next) guard.current = null
    }
  }, [])
  const mayNavigate = useCallback(() => guard.current?.() !== false, [])
  return <Context.Provider value={{ register, mayNavigate }}>{children}</Context.Provider>
}

// The existing screen exit handler owns confirmations, presence and session cleanup.
export function useCoachNavigationGuard(beforeNavigate: BeforeNavigate | undefined) {
  const register = useContext(Context)?.register
  const latest = useRef(beforeNavigate)
  useLayoutEffect(() => {
    latest.current = beforeNavigate
  })
  const enabled = !!beforeNavigate
  useLayoutEffect(() => {
    if (enabled && register) return register(() => latest.current?.())
  }, [enabled, register])
}

export function useCoachNavigation() {
  return useContext(Context)
}
